import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errMsg, fmtUSD2 } from "@/lib/api";
import { PRODUCT_TYPES, productTypeLabel } from "@/lib/constants";
import { Pill } from "@/components/app/shared";

const blank = { name: "", productType: "SPONSORED_WEB", defaultUnitPrice: 0, description: "", active: true };

export default function ProductsDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await api.get("/products")).data,
    enabled: open,
  });
  const [editing, setEditing] = useState(null);
  const products = data?.products || [];

  const save = useMutation({
    mutationFn: async (f) => {
      const body = { ...f, defaultUnitPrice: Number(f.defaultUnitPrice) || 0 };
      if (f.id) return (await api.patch(`/products/${f.id}`, body)).data;
      return (await api.post("/products", body)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); setEditing(null); toast.success("Producto guardado"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => api.delete(`/products/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); toast.success("Producto eliminado"); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-card" data-testid="products-dialog">
        <DialogHeader>
          <div className="kicker">Catálogo comercial</div>
          <DialogTitle className="font-display text-4xl font-normal">Productos & servicios</DialogTitle>
          <DialogDescription>Define el inventario de productos que puedes cotizar: historias branded, campañas en redes, Libro Anual y fees de producción.</DialogDescription>
        </DialogHeader>

        {editing ? (
          <div className="space-y-4 rounded-md border border-border bg-surface-2 p-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2 grid gap-1.5"><Label className="text-xs">Nombre del producto</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="product-name-input" /></div>
              <div className="grid gap-1.5"><Label className="text-xs">Categoría</Label>
                <Select value={editing.productType} onValueChange={(v) => setEditing({ ...editing, productType: v })}>
                  <SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRODUCT_TYPES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid gap-1.5"><Label className="text-xs">Precio base (USD)</Label>
                <Input type="number" min="0" value={editing.defaultUnitPrice} onChange={(e) => setEditing({ ...editing, defaultUnitPrice: e.target.value })} className="font-mono" data-testid="product-price-input" /></div>
              <div className="md:col-span-2 grid gap-1.5"><Label className="text-xs">Descripción comercial</Label>
                <Textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="product-description-input" /></div>
              <label className="flex items-center gap-2.5 text-sm">
                <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="product-active-switch" /> Activo
              </label>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(null)} data-testid="product-cancel-button">Cancelar</Button>
              <Button onClick={() => save.mutate(editing)} disabled={!editing.name.trim() || save.isPending} data-testid="product-save-button" className="flex-1">
                {save.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Guardar producto
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setEditing({ ...blank })} data-testid="product-new-button"><Plus className="mr-1.5 h-3.5 w-3.5" /> Nuevo producto</Button>
            </div>
            <div className="rounded-md border border-border bg-background">
              <Table>
                <TableHeader>
                  <TableRow>
                    {["Producto", "Categoría", "Precio base", "Estado", ""].map((h) => <TableHead key={h} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? [...Array(3)].map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
                  )) : products.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer" onClick={() => setEditing({ ...p })} data-testid="product-row">
                      <TableCell className="text-sm font-medium">{p.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{productTypeLabel(p.productType)}</TableCell>
                      <TableCell className="font-mono text-sm">{fmtUSD2(p.defaultUnitPrice)}</TableCell>
                      <TableCell><Pill tone={p.active ? "mint" : "neutral"}>{p.active ? "Activo" : "Pausado"}</Pill></TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => del.mutate(p.id)} aria-label="Eliminar" data-testid="product-delete-button">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!isLoading && products.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">Aún no hay productos. Agrega uno para empezar a cotizar.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
